<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCrmStore } from 'dashboard/stores/crm';
import { useAdmin } from 'dashboard/composables/useAdmin';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';

const emit = defineEmits(['create-opportunity', 'open-settings']);
const { isAdmin } = useAdmin();
const { t } = useI18n();
const crmStore = useCrmStore();

const funnelTabs = computed(() =>
  crmStore.funnels
    .filter(f => f.active !== false)
    .map(f => ({ label: f.name, id: f.id }))
);

const activeFunnelIndex = computed(() => {
  const idx = funnelTabs.value.findIndex(
    f => f.id === crmStore.currentFunnelId
  );
  return idx >= 0 ? idx : 0;
});

const handleTabChanged = tab => {
  const funnel = crmStore.funnels.find(f => f.id === tab.id);
  if (funnel) crmStore.selectFunnel(funnel.id);
};
</script>

<template>
  <div
    class="flex items-center gap-3 px-4 py-2.5 border-b border-n-weak bg-n-surface-1 shrink-0 min-h-[52px]"
  >
    <TabBar
      v-if="crmStore.funnels.length > 0"
      :tabs="funnelTabs"
      :initial-active-tab="activeFunnelIndex"
      @tab-changed="handleTabChanged"
    />
    <button
      v-else-if="isAdmin"
      class="text-sm border border-dashed border-n-weak text-n-slate-11 hover:border-woot-500 hover:text-woot-500 px-3 py-1 rounded transition-colors"
      @click="emit('open-settings')"
    >
      + {{ t('CRM.TOPBAR.CREATE_FUNNEL') }}
    </button>

    <div class="flex-1" />

    <button
      class="text-sm bg-woot-500 hover:bg-woot-600 text-white px-4 py-1.5 rounded-lg transition-colors duration-150 font-medium"
      @click="emit('create-opportunity')"
    >
      {{ t('CRM.TOPBAR.NEW_OPPORTUNITY') }}
    </button>

    <button
      v-if="isAdmin"
      class="text-n-slate-11 hover:text-n-slate-12 transition-colors duration-150 p-1.5 rounded-lg hover:bg-n-slate-3"
      @click="emit('open-settings')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    </button>
  </div>
</template>
